"use client"
import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { ArrowBack, Download, ErrorOutline, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, Button, CircularProgress, Container, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme, } from '@mui/material';
import type { SharedProps, SnackbarKey, VariantType } from 'notistack';
import { useSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import Log from '@/Logger';
import { useConfirmDialog } from '@/components/ui/util/useConfirmDialog';

import {
    useEWBCI, useEWBCIUtils,
    ConnectionStatus,
    type FormDocument, type FormField,
} from '@/feature/ewbci';

import { type DeviceInfo } from '@/hooks/useDeviceList';

import { useFormDocument, FormDocumentStateStatus, } from './hooks/useFormDocument';
import { useFormSubscription, FormSubscriptionStatus, } from './hooks/useFormSubscription';
import { hasConflictFields, hasEditingFields, useFieldUpdateManager } from './hooks/useFieldUpdateManager';
import { usePageBackground } from './hooks/usePageBackground';

import { type FormFieldDef, DraftStatus, type DraftMap } from './types';
import { detectValidateType } from './validateForm';

import { FieldInput } from './components/field-input';
import { FieldConflictDialog } from './components/field-conflict-dialog';
import { DropdownNav, SideNav } from './components/toc-nav';
import { HistoryDialog, type HistoryDialogHandle } from './components/history-dialog';
import * as MSG from './messages';
import { mmToPt, mmToPx } from './utils';
import { isDeepEqual } from '@/util';


const isDebug = import.meta.env.NODE_ENV === "development" || import.meta.env.DEBUG === "true" || import.meta.env.DEBUG === "1";

// // PDF.jsのワーカーを設定（クライアントサイドでのみ実行）アプリ起動時に一回だけ実行される
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url';
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

interface PdfViewerProps {
    docId: string,
    tenantId: string,
    device: DeviceInfo,
    setIsPDFEditVisible?: React.Dispatch<React.SetStateAction<boolean>>
    setIsConflict?: React.Dispatch<React.SetStateAction<boolean>>; // 競合状態を管理するための関数
}

/**
 * 目次情報の定義
 */
type toc = {
    pageId: string;
    name: string;
    index: number; // 0始まり
};

function pickNextPage(docInfo: FormDocument | null, hint?: number): string {
    if (!docInfo?.pages?.length) return "";
    if (!hint) return docInfo.pages[0].id;
    else return docInfo.pages[Math.min(hint - 1, docInfo.pages.length - 1)]?.id ?? "";
}

const LoadingReason = {
    pdfjs: "pdfjs is not loaded...",
    page: "current page is null...",
    pdf: "pdf loading...",
    rendering: "pdf rendering...",
    none: "",
} as const;
type LoadingReason = typeof LoadingReason[keyof typeof LoadingReason];

/**
 * PdfViewerコンポーネント
 * 
 * デバイス上のPDFドキュメントを表示・編集するためのUIコンポーネントです。
 * documentListの子要素で、PDFListやCSVリストと並列な関係。
 * 
 * フォーム送信は先入れ先出しのキューっぽい感じで処理してます。sendFormListに入ったものから順番に処理されます。リクエスト処理自体は並行に進みます。
 * 競合があった場合はconflictformListに追加され、競合ポップアップが表示された後に、再送信する場合は再度sendFormListに追加されます。
 * 
 * @component
 * @param {Object} props
 * @param {string} props.docId - 表示するドキュメントID
 * @param {string} props.serialId - デバイスのシリアルID
 * @param {string} props.deviceId - デバイスID
 * @param {string} props.tenantId - テナントID
 * @param {function} [props.setIsPDFEditVisible] - 編集画面の表示状態を切り替える関数（オプション）
 * @param {boolean} [props.isPdfEditVisible] - 編集画面の表示状態（オプション）
 * @param {function} [props.setIsConflict] - 競合状態を管理する関数（オプション）
 * 
 * @returns {JSX.Element} PDFビューアUI
 */
export default function PdfViewer({ docId, device: { serialId, deviceId, name: deviceName }, tenantId, setIsPDFEditVisible, setIsConflict }: PdfViewerProps) {
    const [pageId, setPageId] = useState<string>("");
    const ewbci = useEWBCI({ serialId, deviceId, tenantId });
    const { docInfo, refresh: refreshDocInfo, status: formDocStatus } = useFormDocument({
        connectionStatus: ewbci.state.connectionStatus,
        executeItpcmd: ewbci.executeItpcmd,
        docId,
        interval: 60000, // 1分ごとにドキュメント情報を更新
    });
    const { patchPageForm, getDocumentFile, getLatestDocumentPDF } = useEWBCIUtils(ewbci);
    // EWBCIセッション情報
    const {
        state: { sessionId, connectionStatus },
    } = ewbci;

    // ページ自動選択用の副作用
    useEffect(() => {
        if (!docInfo?.pages?.length) return;
        if (pageId && docInfo.pages.find((p) => p.id === pageId)) return; // 文書中に存在するページを指している場合は何もしない
        const logger = Log.getLogger();
        // 最初のページを自動選択
        const nextPage = pickNextPage(docInfo);
        if (nextPage !== "") {
            logger.ui.info("PdfViewer", "selectPageAuto", `Auto select page: ${nextPage}`);
            setPageId(nextPage); // 最初のページを選択
        } else {
            // docInfo.pages[0].id が '' の可能性はないはず（為念）
            logger.ui.error("PdfViewer", "selectPageAuto", `Failed to pick next page. docInfo.pages[0] is null.`);
        }
    }, [pageId, docInfo]);

    // 現在表示中のページ情報
    const { currentPage, formDef } = useMemo(() => {
        if (pageId === "" || !docInfo?.pages) return { currentPage: undefined, formDef: undefined };
        const logger = Log.getLogger();
        const currentPage = docInfo?.pages.find((p) => p.id === pageId);
        const formDef = currentPage?.fields?.reduce(
            (obj: Record<string, FormFieldDef>, field: FormField) => ({
                ...obj,
                [field.key]: {
                    ...field,
                    readonly: field.readonly ?? false,
                    validateType: detectValidateType(field.key),
                } as FormFieldDef,
            }),
            {}
        );
        logger.ui.info("PdfViewer", "useMemo", `Current page set to ID: ${currentPage?.id} fields: ${Object.keys(formDef ?? {}).length}}`);
        return { currentPage, formDef };
    }, [pageId, docInfo]);

    // フォームサブスクリプション
    const { status: subscriptionStatus, isSyncing, form: formState, initialForm, pageImage: frontLayer } = useFormSubscription({ hooks: ewbci, pageId, fieldCount: currentPage?.fields?.length ?? 0 });

    // ページ背景PDF管理
    const { pageBackground, error: backgroundError } = usePageBackground({
        docId,
        currentPage,
        getDocumentFile,
    });

    // ページ背景のエラーを反映
    useEffect(() => {
        if (backgroundError) {
            setErrorMsg(backgroundError);
        }
    }, [backgroundError]);

    // フォームフィールド関連
    const { drafts, history, notifyFocusIn, notifyFocusOut, notifyRemoteUpdate, resolveConflict, clearHistory } = useFieldUpdateManager({
        patchPageForm,
        page: currentPage,
        initialForm,
    });

    // フォーム状態の更新を監視し、更新があった場合にリモート更新を通知
    useEffect(() => {
        if (!formState?.lastUpdatedAt || !formState?.lastUpdatedFields) return;
        notifyRemoteUpdate(formState.lastUpdatedFields, formState.lastUpdatedAt);
    }, [formState?.lastUpdatedAt, formState?.lastUpdatedFields, notifyRemoteUpdate]);

    // 競合状態の監視とbeforeunloadイベントの登録
    useEffect(() => {
        const logger = Log.getLogger();
        const hasConflict = hasConflictFields(drafts) || hasEditingFields(drafts);
        // 競合状態を上位層に通知
        setIsConflict?.(hasConflict);

        if (!hasConflict) return;
        // beforeunloadイベントを使って
        // ページを離れる際（タブ/ウィンドウを閉じる、リロードする、別ページに遷移する等）に
        // 競合がある場合は確認ダイアログを表示する。ブラウザの仕様でメッセージは変更できない
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };

        logger.ui.debug("PdfViewer", "onChangeHasConflict", "Registering beforeunload event listener.");
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            logger.ui.debug("PdfViewer", "onChangeHasConflict", "Cleanup beforeunload event listener.");
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [drafts, setIsConflict]);

    /** 目次情報 */
    const tocArray = docInfo?.pages.map((page, idx) => ({
        pageId: page.id,
        name: page.name,
        index: page.index ?? idx,
    })) ?? [];

    // pdf表示のためのパラメータや状態管理
    const [isPdfjsLoaded, setIsPdfjsLoaded] = useState<boolean>(false); // PDF.jsが読み込まれたかどうかのフラグ
    const [isPdfjsBackgroundLoaded, setIsPdfjsBackgroundLoaded] = useState<boolean>(false); // PDF.jsに背景PDFを読み込まれたかどうかのフラグ
    const [loadingReason, setLoadingReason] = useState<LoadingReason>(LoadingReason.none); // ローディング状態の管理
    useEffect(() => {
        // isLoadingの依存関係
        let reason: LoadingReason = LoadingReason.none;
        if (isPdfjsLoaded === false) reason = LoadingReason.pdfjs; // PDF.jsが読み込まれていない
        else if (!currentPage) reason = LoadingReason.page; // ページ情報が未取得
        else if (pageBackground === null) reason = LoadingReason.pdf; // ページ背景取得中
        else if (isPdfjsBackgroundLoaded === false) reason = LoadingReason.rendering; // ページ背景取得中
        setLoadingReason(reason);
    }, [isPdfjsLoaded, currentPage, pageBackground, isPdfjsBackgroundLoaded]);
    useEffect(() => {
        if (pageBackground === null) setIsPdfjsBackgroundLoaded(false);
        if (pageBackground === "") setIsPdfjsBackgroundLoaded(true);
    }, [pageBackground]);

    // 警告・エラーメッセージ関連
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [errorMsg, setErrorMsg] = useState<string | null>(null); // エラーメッセージ
    const [warnMsg, setWarnMsg] = useState<string | string[] | null>(null); // 警告メッセージ。エラーメッセージと並行して表示される
    const MsgListRef = useRef<{ sbkey: SnackbarKey, type: VariantType, message: string | React.JSX.Element, originalMessage: string | string[] }[]>([]); // Snackbarに表示中のメッセージリストの参照
    useEffect(() => {
        const defaultSnackbarOption: SharedProps = {
            variant: "info", // info/warning/error/success
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, // 画面左下に表示
            autoHideDuration: null, // 自動で消えない
        };
        if (errorMsg) {
            if (!MsgListRef.current.find((m) => m.originalMessage === errorMsg && m.type === "error")) { // 既に表示中のメッセージはスキップ
                MsgListRef.current.push({
                    sbkey: enqueueSnackbar(errorMsg, { ...defaultSnackbarOption, variant: "error" }),
                    type: "error",
                    message: errorMsg,
                    originalMessage: errorMsg,
                });
            }
        }
        if (warnMsg) {
            const message = Array.isArray(warnMsg)
                ? <>
                    {warnMsg.map((msg, i) => (
                        <React.Fragment key={`warnMsg[${i}]`}>
                            {msg} {(i < warnMsg.length - 1) && (<br />)}
                        </React.Fragment>
                    ))}
                </>
                : warnMsg;
            if (!MsgListRef.current.find((m) => isDeepEqual(m.originalMessage, warnMsg) && m.type === "warning")) { // 既に表示中のメッセージはスキップ
                MsgListRef.current.push({
                    sbkey: enqueueSnackbar(message, { ...defaultSnackbarOption, variant: "warning" }),
                    type: "warning",
                    message,
                    originalMessage: warnMsg,
                });
            }
        }
        for (const msg of MsgListRef.current) {
            if (msg.type === "error" && errorMsg && isDeepEqual(errorMsg, msg.originalMessage)) continue;
            if (msg.type === "warning" && warnMsg && isDeepEqual(warnMsg, msg.originalMessage)) continue;
            // メッセージが現在のerrorMsg/warnMsgに含まれていない場合はスナックバーを閉じる
            closeSnackbar(msg.sbkey);
            MsgListRef.current = MsgListRef.current.filter((m) => m.sbkey !== msg.sbkey);
        }
    }, [errorMsg, warnMsg, enqueueSnackbar, closeSnackbar]);

    //pdfビューの見た目関連
    const [scale, setScale] = useState<number>(1);

    // その他
    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog(); // 確認ダイアログのフック
    const [isDownloading, setIsDownloading] = useState(false); // ダウンロード中かどうかのフラグ

    const [headerEl, setHeaderEl] = useState<HTMLDivElement | null>(null);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));//ウインドウがスマホサイズかを判定する

    // メインコンテンツの高さをヘッダとフッタの高さに応じて変化させる
    const [viewerHeight, setViewerHeight] = useState<string>('auto');
    const handleResize = useCallback(() => {
        if (!headerEl) return;
        setViewerHeight(`calc(100% - ${getComputedStyle(headerEl).height})`); // ヘッダ/フッタの高さを引いた残りの高さを指定
    }, [headerEl]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        // クリーンアップ
        return () => (window.removeEventListener('resize', handleResize));
    }, [handleResize]);
    useLayoutEffect(handleResize, [handleResize, warnMsg, headerEl]); // 初回とwarnMsgが変更されたタイミングで再計算が必要

    // 編集履歴ダイアログのハンドラ参照
    const historyDialogHandle = useRef<HistoryDialogHandle>(null);

    /**
     * PDF.jsの読み込みが完了するまで待機します。
     * TODO: 読込完了条件を見直す
     */
    const waitUntilPdfjsLoaded = useCallback(async () => {
        const logger = Log.getLogger();
        try {
            const response = await fetch(pdfjs.GlobalWorkerOptions.workerSrc, { method: "HEAD" });
            if (!response.ok) {
                setErrorMsg(MSG.errUndefined);
                logger.ui.error("PdfViewer", "waitUntilPdfjsLoaded", `PDF.js worker loading failed. response: ${response}`);
                return;
            }
            setIsPdfjsLoaded(true); // PDF.jsが正常に読み込まれた
            logger.ui.info("PdfViewer", "waitUntilPdfjsLoaded", `PDF.js worker loaded successfully.`);
        } catch (error) {
            setErrorMsg(MSG.errUndefined);
            logger.ui.error("PdfViewer", "waitUntilPdfjsLoaded", `PDF.js worker loading failed. error: ${error}`);
        }
    }, []);

    // 初期化処理 ※初回のみ実行されます。
    useEffect(() => {
        // PDF.jsの読み込み？
        waitUntilPdfjsLoaded();

        return () => {
            // 画面移動時などに変数のリセットを行う
            setErrorMsg(null);
            setWarnMsg(null);
            // 表示中のメッセージをすべて閉じる
            if (MsgListRef.current.length > 0) {
                MsgListRef.current.forEach((msg) => {
                    closeSnackbar(msg.sbkey);
                });
                MsgListRef.current = [];
            }
            // Blob URLのクリーンアップのためにタブを閉じる直前にリセット処理が呼ばれるようにしておく
            setPageId("");
            clearHistory(); // 履歴をリセット
        };
    }, [clearHistory, closeSnackbar, waitUntilPdfjsLoaded]);

    // 接続状態が変化したときの処理
    useEffect(() => {
        switch (connectionStatus) {
            case ConnectionStatus.SERVER_DISCONNECTED:
                setWarnMsg(MSG.warnServerDisconnected);
                break;
            case ConnectionStatus.SERVER_CONNECTING:
                setWarnMsg(MSG.warnServerConnecting);
                break;
            case ConnectionStatus.SERVER_CONNECTED:
                setErrorMsg(null);
                setWarnMsg(null);
                break;

            case ConnectionStatus.EWB_DISCONNECTED:
                setWarnMsg(prev => {
                    if (!(prev instanceof Array) || prev.length === 0) return MSG.warnNWError(deviceName);
                    for (const msg of MSG.warnEwbConnectRetry(deviceName)) {
                        if (!prev.includes(msg))
                            return MSG.warnNWError(deviceName);
                    }
                    return prev;
                });
                break;
            case ConnectionStatus.EWB_CONNECTING: {
                let timeoutId: NodeJS.Timeout | undefined = undefined;
                setWarnMsg(prev => {
                    // 既に30秒後に表示されるメッセージが表示されている場合はそのまま
                    if (isDeepEqual(prev, MSG.warnEwbConnectRetry(deviceName))) return prev;

                    // 30秒後にメッセージを更新する
                    timeoutId = setTimeout(() => {
                        setWarnMsg((prev) => (prev !== MSG.warnEwbConnecting(deviceName))
                            ? prev // 既にメッセージが変わっている場合は無視
                            : MSG.warnEwbConnectRetry(deviceName)
                        );
                    }, 3000);
                    // 最初は通常の接続中メッセージを表示
                    return MSG.warnEwbConnecting(deviceName);
                });

                return () => timeoutId && clearTimeout(timeoutId); // クリーンアップ
            }
            case ConnectionStatus.CONNECTED:
                switch (subscriptionStatus) {
                    case FormSubscriptionStatus.Initializing:
                        setWarnMsg(MSG.warnEwbFetching(deviceName));
                        setErrorMsg(null);
                        break;
                    case FormSubscriptionStatus.Ready:
                        if (isSyncing) {
                            setWarnMsg(MSG.warnEwbFetching(deviceName));
                            setErrorMsg(null);

                        } else {
                            setWarnMsg(null);
                            setErrorMsg(null);
                            setLoadingReason(LoadingReason.none);
                        }
                        break;
                    case FormSubscriptionStatus.Unavailable:
                        setWarnMsg(MSG.warnEwbUnavailable(deviceName));
                        break;
                    default:
                        break;
                }
                break;
            case ConnectionStatus.NONE:
            case ConnectionStatus.DISCONNECTED:
            default:
                setErrorMsg(null);
                setWarnMsg(null);
                break;
        }
    }, [connectionStatus, deviceName, subscriptionStatus, isSyncing]);

    /**
     * 表示中のPDF文書を Agent から S3 にアップロードし、その PDF を S3 よりダウンロードしてローカルに保存する。
     */
    const handleClickDownload = useCallback(async () => {
        const logger = Log.getLogger();
        logger.ui.info("PdfViewer", "handleClickDownload", `Downloading document with docId: ${docId}, serialId: ${serialId}`);
        setIsDownloading(true); // ダウンロード中フラグを ON（UI 表示制御用）

        try {
            // ダウンロード時のPDFファイル名設定（文書名が未設定なら "Untitled_Document_{xxx}.pdf"）
            const fileName = docInfo?.name || `Untitled_Document_${docId}.pdf`;

            // PDFダウンロード用の一時的なURLを取得
            const blobUrl = await getLatestDocumentPDF(docId);
            // PDF をユーザーのローカルに保存
            try {
                saveAs(blobUrl, fileName);
            } finally {
                URL.revokeObjectURL(blobUrl);
            }
        } catch (error) {
            logger.ui.error("PdfViewer", "handleClickDownload", `Failed to download document: ${error}`);
            setErrorMsg(MSG.errEwbDisconnected(deviceName));
        } finally {
            setIsDownloading(false); // ダウンロード中フラグを OFF（UI 表示制御用）
        }
    }, [docId, docInfo?.name, getLatestDocumentPDF, serialId, deviceName]);

    // 拡大・縮小ボタンのハンドラ
    const zoomIn = () => setScale((prevScale) => Math.min(prevScale + 0.1, 3));
    const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.1, 0.3));

    /* ページ移動前に競合や編集中のフィールドがあるか確認し、必要に応じて警告ダイアログを表示する関数 */
    const checkBeforeNavigate = useCallback((fn: string, drafts: DraftMap, navigate: () => void) => {
        const logger = Log.getLogger();
        let warn: any = null;
        let reason: any = null;
        if (hasConflictFields(drafts)) { warn = MSG.warnConflict; reason = 'conflict'; }
        if (hasEditingFields(drafts)) { warn = MSG.warnEditing; reason = 'editing'; }
        if (warn != null) {
            // 競合がある場合は警告を出す
            setConfirmDialogMessage(warn, true);
            openConfirmDialog().then((resp) => {
                switch (resp) {
                    case "confirm":
                        // 競合があっても強制的に進むことを選択
                        logger.ui.info("PdfViewer", fn, `Confirmed navigation despite ${reason}s.`);
                        navigate();
                        break;
                    case "cancel":
                        // キャンセルされた場合は何もしない
                        logger.ui.info("PdfViewer", fn, `Cancelled navigation due to ${reason}s.`);
                        break;
                    default:
                        // 通常は通らないはずの分岐
                        // ConfirmDialogResultの変更を検知するための型チェックであり、コンパイルエラー時はswitch文を見直す必要があります。
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const _exhaustiveCheck: never = resp;
                        break;
                }
            });
        } else {
            // 競合がない場合はそのまま進む
            navigate();
        }
    }, [openConfirmDialog, setConfirmDialogMessage]);

    /**
     * しおり押下でのページ切り替えの際に発火する関数。サイドバー(右)からもプルダウン(左上)からも発火する。
     * @param selectedToc 切り替えるページのしおりオブジェクト
     */
    const handleSelectToc = useCallback((selectedToc: toc) => {
        const logger = Log.getLogger();
        logger.ui.info("PdfViewer", "handleSelectToc", `Switching to page ID: ${selectedToc.pageId})`);
        if (selectedToc.pageId === pageId) return; // 既に表示中のページだった場合は無視

        // changePage
        checkBeforeNavigate("handleSelectToc", drafts, () => {
            setIsPdfjsBackgroundLoaded(false); // ページ背景が読み込まれたフラグをリセット
            setPageId(selectedToc.pageId); // ページIDを更新
        });
    }, [pageId, drafts, checkBeforeNavigate]);

    // 戻るボタンがクリックされたときの処理
    const handleClickBackToPdfList = useCallback(() => {
        const logger = Log.getLogger();
        logger.ui.info('PdfViewer', 'handleClickBackToPdfList', `Returning to PDF list with docId: ${docId}, serialId: ${serialId}`);

        // backToPdfList
        checkBeforeNavigate("handleClickBackToPdfList", drafts, () => {
            setIsPDFEditVisible?.(false);
        });

    }, [serialId, docId, drafts, checkBeforeNavigate, setIsPDFEditVisible]);

    // PDFドキュメントの読み込み成功時のコールバック
    const onDocumentLoadSuccess = useCallback(() => {
        if (!isPdfjsBackgroundLoaded) {
            // フラグ更新
            setIsPdfjsBackgroundLoaded(true);

            // ウィンドウ幅を取得
            const pdfWidthMm = currentPage?.size.w; // mm単位
            const pdfWidthPx = pdfWidthMm ? mmToPx(pdfWidthMm) : 0;
            const windowWidthPx = typeof window !== "undefined" ? window.innerWidth : 0;
            // スケールをウィンドウ幅に合わせて設定
            const widthRatio = pdfWidthPx ? windowWidthPx / pdfWidthPx : 0;
            setScale(Math.max(0.3, Math.min(3, Math.floor(widthRatio * 0.8 * 10) / 10)));
        }
    }, [currentPage?.size.w, isPdfjsBackgroundLoaded]);

    // PDFドキュメントの読み込み失敗時のコールバック
    const onDocumentLoadError = useCallback((err: Error) => {
        const logger = Log.getLogger();
        logger.ui.error("PdfViewer", "onDocumentLoadError", `Failed to load or render PDF document: ${err.message}`);
        setWarnMsg("PDFを描画できません。");
        setIsPdfjsBackgroundLoaded(true); // エラー表示のためにロード完了扱いにする
    }, []);

    // PDFコンテンツのレンダリング
    // formDefがundefinedの場合は空のコンポーネントを返す
    // formDefが存在する場合はPDFページとフォームフィールドを重ねて表示する
    // frontLayerが存在する場合はさらにその上にストローク等を重ねる
    // draftsにはこのページのフォームフィールドの下書き情報が入っている
    const renderContentView = useCallback(() => {
        return (
            <Box sx={{
                width: "fit-content",
                height: "fit-content",
            }}>
                <Document
                    file={pageBackground}
                    onLoadError={onDocumentLoadError}
                    loading={null}
                    noData={null}
                    error={null}
                > {/* react-pdf.Documentで囲う */}
                    <Box
                        key={`page_${pageId}`}
                        sx={{
                            mb: 3,
                            width: "fit-content",
                            height: "fit-content",
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            userSelect: "none",
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            align="center"
                            sx={{ display: "block", mb: 1 }}
                        >
                        </Typography>
                        {/* PDFページ(背景) */}
                        <Page
                            pageNumber={1}
                            scale={scale}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={onDocumentLoadSuccess}
                            onRenderError={onDocumentLoadError}
                            className="pdf-page"
                            error={null}
                            loading={null}
                        />
                        {/* このページのテキストボックスを絶対配置で重ねる */}
                        {Object.values(formDef ?? {}).map((field) => {
                            const draft = drafts[field.key];
                            return (
                                <FieldInput
                                    key={`fi-${field.key}`}
                                    scale={scale}
                                    field={field}
                                    draftStatus={draft?.status ?? DraftStatus.None}
                                    draftLocal={draft?.local}
                                    notifyFocusIn={notifyFocusIn}
                                    notifyFocusOut={notifyFocusOut}
                                />
                            );
                        })}
                        {/* 競合が発生しているフィールドに対するダイアログを表示 */}
                        {Object.values(formDef ?? {}).map((field) => {
                            if (drafts[field.key]?.status !== DraftStatus.Conflict) return null;
                            return (
                                <FieldConflictDialog
                                    key={`fcd-${field.key}`}
                                    field={field}
                                    draft={drafts[field.key]}
                                    scale={scale}
                                    pagesize={currentPage?.size ?? { w: 0, h: 0 }}
                                    resolveConflict={resolveConflict}
                                />
                            );
                        })}
                        {/* ストローク等が描かれているFrontLayer(SVG)を表示 */}
                        <img
                            src={frontLayer?.blobUrl ?? ""}
                            alt="Front Layer"
                            style={{
                                position: "absolute",
                                width: "100%",
                                height: "100%",
                                zIndex: 2, // フォームフィールドよりも前面に表示
                                display: frontLayer ? "block" : "none",
                                pointerEvents: "none", // 画像の上での操作を無効化
                            }}
                        ></img>
                    </Box>
                </Document>
            </Box>
        );
    }, [pageBackground, onDocumentLoadError, pageId, scale, onDocumentLoadSuccess, formDef, frontLayer, drafts, notifyFocusIn, notifyFocusOut, currentPage?.size, resolveConflict]);

    // PDF.jsが読み込まれていない場合はローディング表示を行う
    if (!isPdfjsLoaded) return <div>読み込み中...</div>;

    const isRefreshEnabled = formDocStatus === FormDocumentStateStatus.Success;
    return (
        <Box
            className="PdfViewer"
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch", // 幅いっぱいに広げる
                minWidth: 0,
                boxSizing: "border-box",
                position: "relative",
            }}
        >
            {/* ヘッダ領域 */}
            <Container
                className="PdfViewerHeader"
                ref={setHeaderEl}
                maxWidth={false}
                sx={{
                    flex: "0 0 fit-content",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch", // 幅いっぱいに広げる
                    minWidth: 0,
                    marginLeft: "inherit", // 左の余白をなくす
                }}
                style={{ padding: 0 }} // paddingを調整
            >

                {/* コントロールパネル */}
                <Paper
                    className="PdfViewerToolbar"
                    elevation={2}
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        mb: 1,
                        p: 1,
                        pr: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(8px)",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={0.1}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        {/* タイトルバー(左側) */}
                        <Stack direction="row" spacing={0} alignItems="center"                        >
                            {!isXs && (
                                // スマホサイズではない場合は戻るボタンを表示
                                <Button
                                    variant="outlined"
                                    startIcon={<ArrowBack />}
                                    size="small"
                                    onClick={() => {
                                        handleClickBackToPdfList();
                                    }}
                                    sx={{ textWrap: 'nowrap', flexShrink: 1 }}

                                >
                                    戻る
                                </Button>
                            )}
                            <Stack
                                direction="row"
                                spacing={0}
                                alignItems="center"
                                justifyContent="center"
                                flexWrap="wrap"
                            >
                                {/* 目次ドロップダウン */}
                                <DropdownNav docName={docInfo?.name} tocArray={tocArray} currentPageId={pageId} isRefreshEnabled={isRefreshEnabled} handleRefreshDocInfo={refreshDocInfo} handleSelectToc={handleSelectToc} />
                            </Stack>
                        </Stack>

                        {/* ツールバー(右側) */}
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="flex-end"
                            justifySelf="flex-end"
                            flexWrap="wrap"
                        >
                            <Stack direction="row" spacing={1} alignItems="center" justifySelf="flex-end">
                                {Object.values(drafts).some((d) => d.status === DraftStatus.Sending) && loadingReason === LoadingReason.none && !errorMsg ? (
                                    <Tooltip title="編集中...">
                                        <span>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" color="success.main" sx={{ mr: 1 }}>

                                    </Typography>
                                )}

                                <Tooltip title="編集履歴">
                                    <span>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => historyDialogHandle.current?.open()}
                                            sx={{ ml: 1 }}
                                        >
                                            編集履歴
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" justifySelf="flex-end">
                                <Tooltip title="縮小">
                                    <span>
                                        <IconButton size="small" onClick={zoomOut} disabled={!pageId} aria-label="縮小">
                                            <ZoomOut fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Typography variant="body2" sx={{ minWidth: 40, textAlign: "center" }}>
                                    {Math.round(scale * 100)}%
                                </Typography>

                                <Tooltip title="拡大">
                                    <span>
                                        <IconButton size="small" onClick={zoomIn} disabled={!pageId} aria-label="拡大">
                                            <ZoomIn fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {/* DAB-1652 暫定対応のため一時的に非表示
                                <Tooltip title={isDownloading ? "ダウンロード中..." : "ダウンロード"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleClickDownload()}
                                            aria-label="ダウンロード"
                                            disabled={
                                                isDownloading || // 既にダウンロード処理実行中
                                                connectionStatus !== ConnectionStatus.CONNECTED // eWb,Lantanaに接続されていない状態
                                            }
                                        >
                                            {isDownloading ? (
                                                <CircularProgress size={18} /> // スピナー表示
                                            ) : (
                                                <Download fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip> 
                                */}
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>

            {/* ボディ(Viewer|Sidebar) */}
            <Box
                className="PdfViewerBody"
                sx={{
                    flex: "1 1 auto",
                    width: "100%",
                    height: viewerHeight,
                    display: "flex",
                    position: "relative",
                    flexDirection: "row",
                    alignItems: "stretch", // 高さいっぱいに広げる
                    justifyContent: "stretch", // 横幅いっぱいに広げる
                    userSelect: "none",
                }}
            >
                {/* デバッグ用ラベル */}
                {isDebug ? (
                    <Box sx={{
                        top: 0, left: "0.6em",
                        position: "absolute",
                        zIndex: 1000,
                        pointerEvents: "none",
                        userSelect: "none",
                    }}>
                        <Typography sx={{ color: "#0008" }}>
                            &nbsp;{`${import.meta.env.NODE_ENV}${import.meta.env.DEBUG ?? ""}`}
                            &nbsp;[{sessionId ?? "-"}]
                            &nbsp;{ewbci.state.connectionStatus},
                            &nbsp;&nbsp;doc:{formDocStatus},
                            &nbsp;&nbsp;page:{subscriptionStatus !== FormSubscriptionStatus.Ready
                                ? subscriptionStatus
                                : (isSyncing
                                    ? "WaitForInitialData"
                                    : "Ready")
                            }
                        </Typography>
                    </Box>
                ) : null}
                {/* コンテンツ描画部 */}
                <Box
                    className="PdfViewerContent"
                    sx={{
                        width: "100%",
                        height: "100%",
                        background: "#fafafa",
                        borderRadius: 2,
                        boxShadow: 1,
                        p: 2,
                        whiteSpace: "nowrap",
                        overflow: "auto",
                    }}
                >
                    <Box
                        className="PdfViewerContentScrollArea"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "fit-content",
                            height: "fit-content",
                            minWidth: "100%",
                            minHeight: "100%",
                            position: "relative", // LoadingやErrorMessageの絶対配置用
                        }}
                    >
                        {errorMsg ? ( // エラーメッセージがある場合はエラー画面を表示する
                            <ErrorMessage handleClickBackToPdfList={handleClickBackToPdfList} />
                        ) : loadingReason !== LoadingReason.none ? ( // ロード中の場合はローディング画面を表示する
                            <Loading reason={loadingReason} />
                        ) : !pageBackground ? ( // 背景PDFが読み込まれていない場合は白紙キャンバスを表示する
                            <Blank
                                pageId={pageId}
                                widthPt={mmToPt(currentPage?.size.w ?? 0) * scale}
                                heightPt={mmToPt(currentPage?.size.h ?? 0) * scale}
                                frontLayer={frontLayer}
                            />
                        ) : // 背景PDFが正常に読み込まれた場合はPDF編集画面を表示する
                            renderContentView()
                        }
                    </Box>
                </Box>

                {/* 目次サイドバー */}
                <SideNav docName={docInfo?.name} tocArray={tocArray} currentPageId={pageId} handleSelectToc={handleSelectToc} />
            </Box>

            {/* 編集履歴ダイアログ */}
            <HistoryDialog history={history} ref={historyDialogHandle} />
            <ConfirmDialog title='注意' />
        </Box >
    );
}

/** エラー表示 */
const ErrorMessage: React.FC<{ handleClickBackToPdfList: () => void }> = ({ handleClickBackToPdfList }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 100,
            background: "rgba(255,255,255,0.85)",
            minWidth: "fit-content",
        }}
    >
        <ErrorOutline color="error" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6" gutterBottom>
            エラーが発生しました
        </Typography>
        <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => {
                handleClickBackToPdfList();
            }}
        >
            ドキュメント選択に戻る
        </Button>
    </Box>
);

/** ローディング表示 */
const Loading: React.FC<{ reason: LoadingReason }> = ({ reason }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 100,
            background: "rgba(255,255,255,0.7)",

        }}
    >
        <CircularProgress size={64} />
        {/* デバッグ用ラベル */}
        {isDebug && reason !== LoadingReason.none && (
            <Typography sx={{ color: "#0008" }}>{reason}</Typography>
        )}
    </Box>
);

/** 背景なし(白紙キャンバス+ストローク等のFrontLayerを描画する) */
const Blank: React.FC<{
    pageId: string;
    widthPt: number;
    heightPt: number;
    frontLayer: {
        blobUrl: string;
        lastUpdatedAt: Date;
    } | null;
}> = ({ pageId, widthPt, heightPt, frontLayer }) => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box
            key={`blank_page_${pageId}`}
            sx={{
                mb: 3,
                position: "relative",
                display: "flex",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    width: widthPt,
                    height: heightPt,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    position: "relative",
                    overflow: "auto",
                }}
            ></Box>
            {/* ストローク等が描かれているFrontLayer(SVG)を表示 */}
            {frontLayer ? (
                <img
                    src={frontLayer?.blobUrl ?? ""}
                    alt="Front Layer"
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        zIndex: 10, // フォームフィールドよりも前面に表示
                        display: frontLayer ? "block" : "none",
                        pointerEvents: "none", // 画像の上での操作を無効化
                    }}
                ></img>
            ) : (
                <></>
            )}
        </Box>
    </Box>
);
